import React, { useState } from 'react';
import { Layers, PieChart, Info, Car, MapPin, Users, Award, Calendar, TrendingUp, Clock, Shield } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для отображения детализации расчета премии ОСАГО
 */
const PremiumBreakdown = ({ breakdown }) => {
  const [showDetails, setShowDetails] = useState(false);
  
  if (!breakdown) {
    return null;
  }

  // Получаем название типа автомобиля
  const getCarTypeName = (carType) => {
    const carTypes = {
      passenger: 'Легковой автомобиль',
      truck: 'Грузовой автомобиль',
      bus: 'Автобус',
      motorcycle: 'Мотоцикл',
      tractor: 'Трактор и спецтехника',
      trailer: 'Прицеп'
    };
    return carTypes[carType] || 'Автомобиль';
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <PieChart className="w-5 h-5 mr-2 text-emerald-500" />
          Детализация расчета ОСАГО
        </h3>

        {/* Кнопка переключения детализации */}
        <button
          type="button"
          onClick={() => setShowDetails(prev => !prev)}
          className="text-sm font-medium text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 flex items-center"
          aria-expanded={showDetails}
        >
          {showDetails ? 'Скрыть детали' : 'Показать детали расчета'}
          <Layers className="ml-1.5 w-4 h-4" />
        </button>
      </div>

      {/* Информационная подсказка */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded p-3 mb-4 flex items-start">
        <Info className="text-blue-500 dark:text-blue-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <p>Визуальное представление расчета полиса ОСАГО с учетом всех применяемых коэффициентов и надбавок.</p>
        </div>
      </div>

      {/* Основные параметры автомобиля */}
      <div className="p-4 mb-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50">
        <div className="flex items-start gap-3">
          <Car className="w-6 h-6 text-gray-400 dark:text-gray-500 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Параметры транспортного средства</h4>
            <div className="text-sm text-gray-600 dark:text-gray-400 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1">
              <div className="flex justify-between">
                <span>Тип ТС:</span>
                <span className="font-medium">{getCarTypeName(breakdown.carType)}</span>
              </div>
              <div className="flex justify-between">
                <span>Мощность:</span>
                <span className="font-medium">{breakdown.enginePower} л.с.</span>
              </div>
              <div className="flex justify-between">
                <span>Базовый тариф:</span>
                <span className="font-medium">{formatCurrency(breakdown.baseRate)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Таблица коэффициентов */}
      <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
          <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700">
            <tr>
              <th scope="col" className="px-4 py-3 font-semibold">Коэффициент</th>
              <th scope="col" className="px-4 py-3 font-semibold">Значение</th>
              <th scope="col" className="px-4 py-3 font-semibold">Описание</th>
              {showDetails && (
                <th scope="col" className="px-4 py-3 font-semibold text-right">Влияние на стоимость</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {/* Территориальный коэффициент */}
            <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100">
              <td className="px-4 py-3 flex items-center">
                <MapPin className="w-4 h-4 mr-2 text-blue-500" />
                <span>{breakdown.factors.territory.name}</span>
              </td>
              <td className="px-4 py-3 font-medium">{formatNumber(breakdown.factors.territory.value, 2)}</td>
              <td className="px-4 py-3">{breakdown.factors.territory.region}</td>
              {showDetails && (
                <td className="px-4 py-3 text-right font-medium">
                  {formatCurrency(breakdown.baseRate * breakdown.factors.territory.value - breakdown.baseRate)}
                </td>
              )}
            </tr>

            {/* Коэффициент возраста и стажа */}
            <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100">
              <td className="px-4 py-3 flex items-center">
                <Users className="w-4 h-4 mr-2 text-indigo-500" />
                <span>{breakdown.factors.ageExperience.name}</span>
              </td>
              <td className="px-4 py-3 font-medium">{formatNumber(breakdown.factors.ageExperience.value, 2)}</td>
              <td className="px-4 py-3">{breakdown.factors.ageExperience.details}</td>
              {showDetails && (
                <td className="px-4 py-3 text-right font-medium">
                  {formatCurrency(breakdown.baseRate * breakdown.factors.territory.value * breakdown.factors.ageExperience.value - breakdown.baseRate * breakdown.factors.territory.value)}
                </td>
              )}
            </tr>

            {/* КБМ */}
            <tr className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100 ${
              breakdown.factors.kbm.value < 1 ? "bg-green-50 dark:bg-green-900/10" : 
              breakdown.factors.kbm.value > 1 ? "bg-red-50 dark:bg-red-900/10" : ""
            }`}>
              <td className="px-4 py-3 flex items-center">
                <Award className="w-4 h-4 mr-2 text-amber-500" />
                <span>{breakdown.factors.kbm.name}</span>
              </td>
              <td className={`px-4 py-3 font-medium ${
                breakdown.factors.kbm.value < 1 ? "text-green-600 dark:text-green-400" : 
                breakdown.factors.kbm.value > 1 ? "text-red-600 dark:text-red-400" : ""
              }`}>
                {formatNumber(breakdown.factors.kbm.value, 2)}
              </td>
              <td className="px-4 py-3">{breakdown.factors.kbm.details}</td>
              {showDetails && (
                <td className={`px-4 py-3 text-right font-medium ${
                  breakdown.factors.kbm.value < 1 ? "text-green-600 dark:text-green-400" : 
                  breakdown.factors.kbm.value > 1 ? "text-red-600 dark:text-red-400" : ""
                }`}>
                  {formatCurrency(
                    breakdown.baseRate * breakdown.factors.territory.value * 
                    breakdown.factors.ageExperience.value * breakdown.factors.kbm.value - 
                    breakdown.baseRate * breakdown.factors.territory.value * 
                    breakdown.factors.ageExperience.value
                  )}
                </td>
              )}
            </tr>

            {/* Период использования */}
            <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100">
              <td className="px-4 py-3 flex items-center">
                <Calendar className="w-4 h-4 mr-2 text-purple-500" />
                <span>{breakdown.factors.period.name}</span>
              </td>
              <td className="px-4 py-3 font-medium">{formatNumber(breakdown.factors.period.value, 2)}</td>
              <td className="px-4 py-3">{breakdown.factors.period.details}</td>
              {showDetails && (
                <td className="px-4 py-3 text-right font-medium">
                  {formatCurrency(
                    breakdown.baseRate * breakdown.factors.territory.value * 
                    breakdown.factors.ageExperience.value * breakdown.factors.kbm.value * 
                    breakdown.factors.period.value - 
                    breakdown.baseRate * breakdown.factors.territory.value * 
                    breakdown.factors.ageExperience.value * breakdown.factors.kbm.value
                  )}
                </td>
              )}
            </tr>

            {/* Аварийность */}
            <tr className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100 ${
              breakdown.factors.accident.value > 1 ? "bg-red-50 dark:bg-red-900/10" : ""
            }`}>
              <td className="px-4 py-3 flex items-center">
                <TrendingUp className="w-4 h-4 mr-2 text-orange-500" />
                <span>{breakdown.factors.accident.name}</span>
              </td>
              <td className={`px-4 py-3 font-medium ${
                breakdown.factors.accident.value > 1 ? "text-red-600 dark:text-red-400" : ""
              }`}>
                {formatNumber(breakdown.factors.accident.value, 2)}
              </td>
              <td className="px-4 py-3">{breakdown.factors.accident.details}</td>
              {showDetails && (
                <td className={`px-4 py-3 text-right font-medium ${
                  breakdown.factors.accident.value > 1 ? "text-red-600 dark:text-red-400" : ""
                }`}>
                  {formatCurrency(
                    breakdown.baseRate * breakdown.factors.territory.value * 
                    breakdown.factors.ageExperience.value * breakdown.factors.kbm.value * 
                    breakdown.factors.period.value * breakdown.factors.accident.value - 
                    breakdown.baseRate * breakdown.factors.territory.value * 
                    breakdown.factors.ageExperience.value * breakdown.factors.kbm.value * 
                    breakdown.factors.period.value
                  )}
                </td>
              )}
            </tr>

            {/* Возраст автомобиля */}
            <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100">
              <td className="px-4 py-3 flex items-center">
                <Clock className="w-4 h-4 mr-2 text-cyan-500" />
                <span>{breakdown.factors.carAge.name}</span>
              </td>
              <td className="px-4 py-3 font-medium">{formatNumber(breakdown.factors.carAge.value, 2)}</td>
              <td className="px-4 py-3">{breakdown.factors.carAge.details}</td>
              {showDetails && (
                <td className="px-4 py-3 text-right font-medium">
                  {formatCurrency(
                    breakdown.baseRate * breakdown.factors.territory.value * 
                    breakdown.factors.ageExperience.value * breakdown.factors.kbm.value * 
                    breakdown.factors.period.value * breakdown.factors.accident.value * 
                    breakdown.factors.carAge.value - 
                    breakdown.baseRate * breakdown.factors.territory.value * 
                    breakdown.factors.ageExperience.value * breakdown.factors.kbm.value * 
                    breakdown.factors.period.value * breakdown.factors.accident.value
                  )}
                </td>
              )}
            </tr>

            {/* Итоговая базовая премия */}
            <tr className="bg-gray-50 dark:bg-gray-700/50 font-medium text-emerald-700 dark:text-emerald-300">
              <td className="px-4 py-3 flex items-center">
                <Shield className="w-4 h-4 mr-2 text-emerald-500" />
                <span>Базовая премия ОСАГО</span>
              </td>
              <td className="px-4 py-3" colSpan={showDetails ? 3 : 2}>
                {formatCurrency(
                  breakdown.baseRate * breakdown.factors.territory.value * 
                  breakdown.factors.ageExperience.value * breakdown.factors.kbm.value * 
                  breakdown.factors.period.value * breakdown.factors.accident.value * 
                  breakdown.factors.carAge.value
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Дополнительные опции, если они есть */}
      {breakdown.additionalOptions && Object.keys(breakdown.additionalOptions).length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center">
            <Shield className="w-4 h-4 mr-2 text-indigo-500" />
            Дополнительные опции
          </h4>
          
          <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
              <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700">
                <tr>
                  <th scope="col" className="px-4 py-3 font-semibold">Опция</th>
                  <th scope="col" className="px-4 py-3 font-semibold text-right">Стоимость</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {breakdown.additionalOptions.extendedCoverage && (
                  <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100">
                    <td className="px-4 py-3">Расширенное покрытие</td>
                    <td className="px-4 py-3 text-right font-medium">{formatCurrency(breakdown.additionalOptions.extendedCoverage)}</td>
                  </tr>
                )}
                
                {breakdown.additionalOptions.noDeductible && (
                  <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100">
                    <td className="px-4 py-3">Без франшизы</td>
                    <td className="px-4 py-3 text-right font-medium">{formatCurrency(breakdown.additionalOptions.noDeductible)}</td>
                  </tr>
                )}
                
                {breakdown.additionalOptions.evacuationService && (
                  <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100">
                    <td className="px-4 py-3">Услуги эвакуации</td>
                    <td className="px-4 py-3 text-right font-medium">{formatCurrency(breakdown.additionalOptions.evacuationService)}</td>
                  </tr>
                )}
                
                {breakdown.additionalOptions.rentalCar && (
                  <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100">
                    <td className="px-4 py-3">Аренда автомобиля при ремонте</td>
                    <td className="px-4 py-3 text-right font-medium">{formatCurrency(breakdown.additionalOptions.rentalCar)}</td>
                  </tr>
                )}
                
                {breakdown.additionalOptions.remotePolicyIssue && (
                  <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100">
                    <td className="px-4 py-3">Дистанционное оформление</td>
                    <td className="px-4 py-3 text-right font-medium">{formatCurrency(breakdown.additionalOptions.remotePolicyIssue)}</td>
                  </tr>
                )}
                
                {/* Итого по дополнительным опциям */}
                <tr className="bg-gray-50 dark:bg-gray-700/50 font-medium text-indigo-700 dark:text-indigo-300">
                  <td className="px-4 py-3">Всего за дополнительные опции</td>
                  <td className="px-4 py-3 text-right">
                    {formatCurrency(
                      Object.values(breakdown.additionalOptions).reduce((sum, val) => sum + val, 0)
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Итоговая стоимость полиса */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg">
        <div className="flex justify-between items-center">
          <h4 className="text-base font-medium text-blue-700 dark:text-blue-300">
            Итоговая стоимость полиса ОСАГО
          </h4>
          <span className="text-xl font-bold text-blue-700 dark:text-blue-300">
            {formatCurrency(
              breakdown.baseRate * breakdown.factors.territory.value * 
              breakdown.factors.ageExperience.value * breakdown.factors.kbm.value * 
              breakdown.factors.period.value * breakdown.factors.accident.value * 
              breakdown.factors.carAge.value +
              (breakdown.additionalOptions ? Object.values(breakdown.additionalOptions).reduce((sum, val) => sum + val, 0) : 0)
            )}
          </span>
        </div>
      </div>
    </div>
  );
};

export default PremiumBreakdown;